import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  DocumentListItemResponse,
  PspBranchResponse,
  baseRelatedPspBranchesSelectors,
  BaseCoreState,
  baseDocumentSelectors,
  DocumentCategoryResponse,
  BaseToastAlertClasses,
  baseRelatedPspBranchActions,
  BaseModalService,
  baseSharedActions,
} from 'base';
import { combineLatest, Subject, noop } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'base-customer-provider-details-master-data',
  templateUrl: './base-customer-provider-details-master-data.component.html',
})
export class BaseCustomerProviderDetailsMasterDataComponent implements OnInit, OnDestroy {
  @Input() pspId: number;
  @ViewChild('documentViewerTemplate') documentViewerTemplate: ElementRef;
  providerDetails: PspBranchResponse;
  documents: { document: DocumentListItemResponse; categoryLabel: string }[] = [];
  activeDocumentSrc: Blob;
  activeDocument: DocumentListItemResponse;
  documentViewerModalRef: NgbModalRef;

  private destroy$ = new Subject();

  constructor(private store: Store<BaseCoreState>, private modalService: BaseModalService) {}

  ngOnInit(): void {
    this.store
      .select(baseRelatedPspBranchesSelectors.getRelatedPspBranchDetails)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((state) => state.data as PspBranchResponse)
      )
      .subscribe((details) => {
        if (details) {
          this.providerDetails = details;
        }
      });

    combineLatest([
      this.store.select(baseDocumentSelectors.getDocumentCategories),
      this.store.select(baseRelatedPspBranchesSelectors.getRelatedPspBranchDocuments),
    ])
      .pipe(
        takeUntil(this.destroy$),
        map(([documentCategoriesState, documentsState]: [any, any]) => [
          documentCategoriesState.data,
          documentsState.data as DocumentListItemResponse[],
        ])
      )
      .subscribe(([documentCategories, documents]) => {
        if (documents && documentCategories) {
          documentCategories = Object.entries(documentCategories as DocumentCategoryResponse).reduce(
            (collector, item) => {
              collector.push({ label: item[1] as string, value: +item[0] });

              return collector;
            },
            [] as { label: string; value: number }[]
          );

          this.documents = (documents as DocumentListItemResponse[]).map((document) => {
            return {
              document,
              categoryLabel: documentCategories.find(
                (category: { label: string; value: number }) => category.value === document.category
              )?.label,
            };
          });
        }
      });

    this.store
      .select(baseRelatedPspBranchesSelectors.getRelatedPspBranchDownloadDocument)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.data),
        map((state) => state.data)
      )
      .subscribe((document) => {
        this.openDocumentViewer(document as Blob);
      });
  }

  ngOnDestroy(): void {
    this.store.dispatch(baseRelatedPspBranchActions.clearDownloadRelatedPspBranchDocument());
    this.destroy$.next(true);
  }

  copyToClipboard(text: string): void {
    if (text) {
      navigator.clipboard.writeText(text).then(() => {
        this.store.dispatch(
          baseSharedActions.addSystemAlert({
            payload: {
              class: BaseToastAlertClasses.success,
              body: 'successMessage.copiedToClipboard',
            },
          })
        );
      });
    }
  }

  downloadDocument(
    event: { id: string; action: string },
    document: { document: DocumentListItemResponse; categoryLabel: string }
  ) {
    this.activeDocument = document.document;
    const openInNewWindow = event.action === 'open';
    this.store.dispatch(
      baseRelatedPspBranchActions.downloadRelatedPspBranchDocument({
        payload: {
          document: event.id || this.activeDocument.id,
          documentType: this.activeDocument.type,
          psp: this.pspId,
          openInNewWindow,
        },
      })
    );
  }

  private openDocumentViewer(document: Blob): void {
    this.activeDocumentSrc = document;
    this.documentViewerModalRef = this.modalService.open(
      {
        bodyTemplate: this.documentViewerTemplate,
        cancelButtonDisplayed: false,
        okButtonDisplayed: false,
        hideHeader: true,
        hideFooter: true,
      },
      { size: 'xl' }
    );
    this.documentViewerModalRef.result.then(noop, noop);
  }
}
